import { useState, useEffect, useCallback } from 'react';
import { motion } from 'motion/react';
import { 
  Users, 
  UserCheck, 
  MessageSquare, 
  CreditCard, 
  AlertTriangle,
  Crown,
  Link2,
  Mail,
  Shield,
  TrendingUp,
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
  UserX,
  UserPlus,
  Loader2,
  Activity,
  DollarSign,
  Clock
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stats Card Component
const StatCard = ({ icon: Icon, label, value, subValue, color = "green" }) => {
  const colorClasses = {
    green: "bg-green-50 text-green-600",
    blue: "bg-blue-50 text-blue-600",
    purple: "bg-purple-50 text-purple-600",
    amber: "bg-amber-50 text-amber-600",
    red: "bg-red-50 text-red-600"
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-sm text-stone-500">{label}</span>
      </div>
      <p className="text-3xl font-serif text-stone-800">{value}</p>
      {subValue && <p className="text-xs text-stone-400 mt-1">{subValue}</p>}
    </div>
  );
};

// Activity Item Component
const ActivityItem = ({ type, description, timestamp }) => {
  const typeConfig = {
    user_registered: { icon: UserPlus, color: "text-green-500" },
    connection_made: { icon: Link2, color: "text-blue-500" },
    report_filed: { icon: AlertTriangle, color: "text-amber-500" },
    payment_completed: { icon: DollarSign, color: "text-purple-500" }
  };

  const config = typeConfig[type] || { icon: Activity, color: "text-stone-500" };
  const Icon = config.icon;

  const formatTime = (ts) => {
    if (!ts) return "";
    const date = new Date(ts);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="flex items-center gap-3 py-2">
      <Icon className={`w-4 h-4 ${config.color}`} />
      <span className="text-sm text-stone-600 flex-1">{description}</span>
      <span className="text-xs text-stone-400">{formatTime(timestamp)}</span>
    </div>
  );
};

// User Row Component
const UserRow = ({ user, onSelect }) => {
  const tierBadge = {
    pen_friend: { label: "Pen Friend", color: "bg-emerald-100 text-emerald-700" },
    correspondent: { label: "Correspondent", color: "bg-purple-100 text-purple-700" },
    postmaster: { label: "Postmaster", color: "bg-amber-100 text-amber-700" }
  };

  const badge = user.membership_tier ? tierBadge[user.membership_tier] : null;

  return (
    <div 
      className="flex items-center gap-4 p-4 hover:bg-stone-50 cursor-pointer border-b border-stone-100 last:border-0"
      onClick={() => onSelect(user)}
    >
      <div className="w-10 h-10 bg-stone-100 rounded-full flex items-center justify-center text-stone-500 font-medium">
        {user.name?.[0] || user.email?.[0] || "?"}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-stone-800 truncate">{user.name || "Unknown"}</p>
          {user.role === "admin" && (
            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Admin</span>
          )}
          {badge && (
            <span className={`px-2 py-0.5 text-xs rounded-full ${badge.color}`}>{badge.label}</span>
          )}
        </div>
        <p className="text-sm text-stone-400 truncate">{user.email}</p>
      </div>
      <div className="text-right">
        {user.profile?.is_suspended && (
          <span className="text-xs text-red-500 flex items-center gap-1">
            <UserX className="w-3 h-3" /> Suspended
          </span>
        )}
        {user.profile?.is_active && !user.profile?.is_suspended && (
          <span className="text-xs text-green-500 flex items-center gap-1">
            <UserCheck className="w-3 h-3" /> Active
          </span>
        )}
      </div>
      <Eye className="w-4 h-4 text-stone-300" />
    </div>
  );
};

// User Detail Modal
const UserDetailModal = ({ user, onClose, onUpdate }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const fetchUserDetails = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/system/users/${user.id}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setDetails(data);
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  const updateRole = async (newRole) => {
    setUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/system/users/${user.id}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });
      if (response.ok) {
        onUpdate();
        fetchUserDetails();
      }
    } catch (error) {
      console.error('Error updating role:', error);
    } finally {
      setUpdating(false);
    }
  };

  const updateMembership = async (tier) => {
    setUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/system/users/${user.id}/membership`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ membership_tier: tier })
      });
      if (response.ok) {
        onUpdate();
        fetchUserDetails();
      }
    } catch (error) {
      console.error('Error updating membership:', error);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-stone-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-serif text-stone-800">{details?.user?.name || user.name}</h2>
            <p className="text-sm text-stone-400">{details?.user?.email || user.email}</p>
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>

        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
          </div>
        ) : details ? (
          <div className="p-6 space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-stone-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-serif text-stone-800">{details.stats?.connections || 0}</p>
                <p className="text-xs text-stone-500">Connections</p>
              </div>
              <div className="bg-stone-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-serif text-stone-800">{details.stats?.messages_sent || 0}</p>
                <p className="text-xs text-stone-500">Messages Sent</p>
              </div>
              <div className="bg-stone-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-serif text-stone-800">{details.stats?.reports_filed || 0}</p>
                <p className="text-xs text-stone-500">Reports Filed</p>
              </div>
              <div className="bg-stone-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-serif text-red-600">{details.stats?.reports_against || 0}</p>
                <p className="text-xs text-stone-500">Reports Against</p>
              </div>
            </div>

            {/* Role Management */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-stone-700">User Role</h3>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={details.user?.role === 'user' ? 'default' : 'outline'}
                  onClick={() => updateRole('user')}
                  disabled={updating}
                  className={details.user?.role === 'user' ? 'bg-stone-800' : ''}
                >
                  User
                </Button>
                <Button
                  size="sm"
                  variant={details.user?.role === 'admin' ? 'default' : 'outline'}
                  onClick={() => updateRole('admin')}
                  disabled={updating}
                  className={details.user?.role === 'admin' ? 'bg-red-600 hover:bg-red-700' : 'text-red-600 border-red-200'}
                >
                  <Shield className="w-4 h-4 mr-1" />
                  Admin
                </Button>
              </div>
            </div>

            {/* Membership Management */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-stone-700">Membership Tier</h3>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={!details.user?.membership_tier ? 'default' : 'outline'}
                  onClick={() => updateMembership(null)}
                  disabled={updating}
                  className={!details.user?.membership_tier ? 'bg-stone-600' : ''}
                >
                  None
                </Button>
                <Button
                  size="sm"
                  variant={details.user?.membership_tier === 'pen_friend' ? 'default' : 'outline'}
                  onClick={() => updateMembership('pen_friend')}
                  disabled={updating}
                  className={details.user?.membership_tier === 'pen_friend' ? 'bg-emerald-600 hover:bg-emerald-700' : 'text-emerald-600 border-emerald-200'}
                >
                  Pen Friend
                </Button>
                <Button
                  size="sm"
                  variant={details.user?.membership_tier === 'correspondent' ? 'default' : 'outline'}
                  onClick={() => updateMembership('correspondent')}
                  disabled={updating}
                  className={details.user?.membership_tier === 'correspondent' ? 'bg-purple-600 hover:bg-purple-700' : 'text-purple-600 border-purple-200'}
                >
                  Correspondent
                </Button>
                <Button
                  size="sm"
                  variant={details.user?.membership_tier === 'postmaster' ? 'default' : 'outline'}
                  onClick={() => updateMembership('postmaster')}
                  disabled={updating}
                  className={details.user?.membership_tier === 'postmaster' ? 'bg-amber-600 hover:bg-amber-700' : 'text-amber-600 border-amber-200'}
                >
                  <Crown className="w-4 h-4 mr-1" />
                  Postmaster
                </Button>
              </div>
            </div>

            {/* Profile Info */}
            {details.profile && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-stone-700">Pen Pal Profile</h3>
                <div className="bg-stone-50 rounded-lg p-4 space-y-2 text-sm">
                  <p><span className="text-stone-500">Pen Name:</span> {details.profile.pen_name}</p>
                  <p><span className="text-stone-500">Location:</span> {details.profile.location_country}</p>
                  <p><span className="text-stone-500">Status:</span> {
                    details.profile.is_suspended ? <span className="text-red-500">Suspended</span> :
                    details.profile.is_active ? <span className="text-green-500">Active</span> :
                    <span className="text-stone-400">Inactive</span>
                  }</p>
                </div>
              </div>
            )}

            {/* Recent Payments */}
            {details.payments?.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-stone-700">Recent Payments</h3>
                <div className="bg-stone-50 rounded-lg divide-y divide-stone-200">
                  {details.payments.map((payment) => (
                    <div key={payment.id || `payment-${payment.description}-${payment.amount}`} className="p-3 flex items-center justify-between text-sm">
                      <span className="text-stone-600">{payment.description}</span>
                      <span className="font-medium text-stone-800">${payment.amount?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </motion.div>
    </div>
  );
};

export default function SystemAdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // User list state
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 0 });

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/system/stats`, {
        credentials: 'include'
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: '20'
      });
      if (searchTerm) params.append('search', searchTerm);
      if (roleFilter) params.append('role', roleFilter);
      if (tierFilter) params.append('membership_tier', tierFilter);

      const response = await fetch(`${API_URL}/api/admin/system/users?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setUsers(data.users || []);
      setPagination(prev => ({
        ...prev,
        total: data.pagination?.total || 0,
        pages: data.pagination?.pages || 0
      }));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [pagination.page, searchTerm, roleFilter, tierFilter]);

  const fetchActivity = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/system/activity?limit=20`, {
        credentials: 'include'
      });
      const data = await response.json();
      setActivity(data.activity || []);
    } catch (error) {
      console.error('Error fetching activity:', error);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      Promise.all([fetchStats(), fetchActivity()]).finally(() => setLoading(false));
    }
  }, [user, fetchStats, fetchActivity]);

  useEffect(() => {
    if (user?.role === 'admin' && activeTab === 'users') {
      fetchUsers();
    }
  }, [user, activeTab, fetchUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchUsers();
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <Shield className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <h1 className="text-xl font-serif text-stone-600">Admin Access Required</h1>
          <p className="text-stone-400 mt-2">Please log in as an administrator.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="system-admin-page">
      {/* Header */}
      <section className="bg-gradient-to-b from-stone-900 to-stone-800 text-white py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8" />
            <h1 className="text-3xl font-serif">System Admin</h1>
          </div>
          <p className="text-stone-300">Platform management and analytics dashboard</p>
        </div>
      </section>

      {/* Tabs */}
      <section className="border-b border-stone-200 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === 'overview' 
                  ? 'border-stone-900 text-stone-900' 
                  : 'border-transparent text-stone-400 hover:text-stone-600'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`py-4 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === 'users' 
                  ? 'border-stone-900 text-stone-900' 
                  : 'border-transparent text-stone-400 hover:text-stone-600'
              }`}
            >
              Users
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`py-4 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === 'activity' 
                  ? 'border-stone-900 text-stone-900' 
                  : 'border-transparent text-stone-400 hover:text-stone-600'
              }`}
            >
              Activity
            </button>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-8 px-6">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
            </div>
          ) : activeTab === 'overview' && stats ? (
            <div className="space-y-8">
              {/* Main Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={Users} label="Total Users" value={stats.users?.total || 0} color="blue" />
                <StatCard icon={UserCheck} label="Active Members" value={stats.users?.active_members || 0} color="green" />
                <StatCard icon={Link2} label="Connections" value={stats.penpal?.active_connections || 0} subValue={`${stats.penpal?.pending_connections || 0} pending`} color="purple" />
                <StatCard icon={DollarSign} label="Total Revenue" value={`$${(stats.revenue?.total_revenue || 0).toFixed(0)}`} subValue={`${stats.revenue?.total_payments || 0} payments`} color="amber" />
              </div>

              {/* Membership Breakdown */}
              <div className="bg-white rounded-xl border border-stone-200 p-6">
                <h3 className="text-lg font-serif text-stone-800 mb-4">Membership Tiers</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-emerald-50 rounded-lg">
                    <p className="text-3xl font-serif text-emerald-700">{stats.users?.by_tier?.pen_friend || 0}</p>
                    <p className="text-sm text-emerald-600">Pen Friends</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-3xl font-serif text-purple-700">{stats.users?.by_tier?.correspondent || 0}</p>
                    <p className="text-sm text-purple-600">Correspondents</p>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-lg">
                    <p className="text-3xl font-serif text-amber-700">{stats.users?.by_tier?.postmaster || 0}</p>
                    <p className="text-sm text-amber-600">Postmasters</p>
                  </div>
                </div>
              </div>

              {/* Secondary Stats */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Pen Pal Stats */}
                <div className="bg-white rounded-xl border border-stone-200 p-6">
                  <h3 className="text-lg font-serif text-stone-800 mb-4">Pen Pal Network</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-stone-500">Active Profiles</span>
                      <span className="font-medium">{stats.penpal?.active_profiles || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-stone-500">Suspended Profiles</span>
                      <span className="font-medium text-red-500">{stats.penpal?.suspended_profiles || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-stone-500">Total Messages</span>
                      <span className="font-medium">{stats.penpal?.messages || 0}</span>
                    </div>
                  </div>
                </div>

                {/* Moderation Stats */}
                <div className="bg-white rounded-xl border border-stone-200 p-6">
                  <h3 className="text-lg font-serif text-stone-800 mb-4">Moderation</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-stone-500">Total Reports</span>
                      <span className="font-medium">{stats.moderation?.total_reports || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-stone-500">Pending Reports</span>
                      <span className="font-medium text-amber-500">{stats.moderation?.pending_reports || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-stone-500">Postmaster Applications</span>
                      <span className="font-medium">{stats.applications?.pending || 0} pending</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <a href="#admin-reports" className="flex items-center gap-3 p-4 bg-white rounded-xl border border-stone-200 hover:border-stone-300 transition-colors">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  <span className="text-sm font-medium">View Reports</span>
                </a>
                <a href="#admin-postmaster" className="flex items-center gap-3 p-4 bg-white rounded-xl border border-stone-200 hover:border-stone-300 transition-colors">
                  <Crown className="w-5 h-5 text-purple-500" />
                  <span className="text-sm font-medium">Applications</span>
                </a>
                <a href="#admin-forwarding" className="flex items-center gap-3 p-4 bg-white rounded-xl border border-stone-200 hover:border-stone-300 transition-colors">
                  <Mail className="w-5 h-5 text-blue-500" />
                  <span className="text-sm font-medium">Forwarding</span>
                </a>
                <button onClick={() => setActiveTab('users')} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-stone-200 hover:border-stone-300 transition-colors">
                  <Users className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium">Manage Users</span>
                </button>
              </div>
            </div>
          ) : activeTab === 'users' ? (
            <div className="space-y-6">
              {/* Search & Filters */}
              <div className="bg-white rounded-xl border border-stone-200 p-4">
                <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[200px] relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <input
                      type="text"
                      placeholder="Search by name or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-stone-500 focus:border-transparent"
                    />
                  </div>
                  <select
                    value={roleFilter}
                    onChange={(e) => { setRoleFilter(e.target.value); setPagination(p => ({...p, page: 1})); }}
                    className="px-4 py-2 border border-stone-200 rounded-lg text-sm"
                  >
                    <option value="">All Roles</option>
                    <option value="user">Users</option>
                    <option value="admin">Admins</option>
                  </select>
                  <select
                    value={tierFilter}
                    onChange={(e) => { setTierFilter(e.target.value); setPagination(p => ({...p, page: 1})); }}
                    className="px-4 py-2 border border-stone-200 rounded-lg text-sm"
                  >
                    <option value="">All Tiers</option>
                    <option value="none">No Membership</option>
                    <option value="pen_friend">Pen Friend</option>
                    <option value="correspondent">Correspondent</option>
                    <option value="postmaster">Postmaster</option>
                  </select>
                  <Button type="submit" size="sm">Search</Button>
                </form>
              </div>

              {/* User List */}
              <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                {users.length === 0 ? (
                  <div className="p-12 text-center">
                    <Users className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                    <p className="text-stone-500">No users found</p>
                  </div>
                ) : (
                  <>
                    {users.map(u => (
                      <UserRow key={u.id} user={u} onSelect={setSelectedUser} />
                    ))}
                    
                    {/* Pagination */}
                    <div className="flex items-center justify-between p-4 border-t border-stone-200 bg-stone-50">
                      <p className="text-sm text-stone-500">
                        Showing {(pagination.page - 1) * 20 + 1} - {Math.min(pagination.page * 20, pagination.total)} of {pagination.total}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPagination(p => ({...p, page: p.page - 1}))}
                          disabled={pagination.page <= 1}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPagination(p => ({...p, page: p.page + 1}))}
                          disabled={pagination.page >= pagination.pages}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : activeTab === 'activity' ? (
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <div className="flex items-center gap-2 mb-6">
                <Activity className="w-5 h-5 text-stone-400" />
                <h3 className="text-lg font-serif text-stone-800">Recent Activity</h3>
              </div>
              {activity.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-500">No recent activity</p>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {activity.map((item) => (
                    <ActivityItem key={item.id || `${item.type}-${item.timestamp}`} {...item} />
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </section>

      {/* User Detail Modal */}
      {selectedUser && (
        <UserDetailModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onUpdate={fetchUsers}
        />
      )}
    </div>
  );
}
